import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Check as CheckIcon } from "@mui/icons-material";
import EstadoChip from "../kit/EstadoChip";
import { usePostulaciones, useResolverPostulacion } from "../../queries/usePostulaciones";
import { AccionPostulacion } from "../../types/postulacion";
import { formatFecha } from "../../utils/fecha";
import { iconoDePostulacion, tonoDePostulacion } from "../../utils/estados";
import { mensajeDeError } from "../../utils/errores";

/**
 * Propuesta de tema hecha por un estudiante, vista por el guía (o el coordinador).
 *
 * Un tema propuesto siempre tiene exactamente una solicitud —la del proponente—, así
 * que en vez de la tabla de `PostulacionesTema` se muestra aquí, con quién lo propuso
 * y las dos acciones. Aceptar aprueba el tema; rechazar lo elimina, por eso se
 * confirma y después se sale de la ficha.
 */
export default function PropuestaTema({
  temaId,
  admiteResolucion,
}: {
  temaId: number;
  /** Regla del backend (`Tema.admite_resolucion`): sin esto no se muestran las acciones. */
  admiteResolucion: boolean;
}) {
  const navigate = useNavigate();
  const { data, isLoading, isError } = usePostulaciones(temaId, true);
  const resolver = useResolverPostulacion(temaId);
  const [confirmar, setConfirmar] = useState<AccionPostulacion | null>(null);
  // Comentario del rechazo: viaja al correo del estudiante. Es opcional, pero acá el
  // tema se elimina, así que sin una razón el proponente no sabe qué proponer distinto.
  const [motivo, setMotivo] = useState("");

  // Es una sola, pero se prefiere la pendiente por si el histórico trajera más de una.
  const propuesta = data?.find((p) => p.estado === "pendiente") ?? data?.[0];

  const abrir = (accion: AccionPostulacion) => {
    setMotivo("");
    setConfirmar(accion);
  };

  const ejecutar = () => {
    if (!confirmar || !propuesta) return;
    const rechazo = confirmar === "rechazar";
    resolver.mutate(
      { id: propuesta.id, accion: confirmar, motivo: rechazo ? motivo : "" },
      {
        onSettled: () => setConfirmar(null),
        // Al rechazar, el tema queda eliminado: no tiene sentido dejar al guía en su ficha.
        onSuccess: () => rechazo && navigate("/app/temas"),
      },
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3, mt: 5 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (isError) {
    return (
      <Alert severity="error" sx={{ mt: 5 }}>
        No se pudo cargar la propuesta.
      </Alert>
    );
  }
  if (!propuesta) return null;

  const pendiente = propuesta.estado === "pendiente";

  return (
    <Box sx={{ mt: 5 }}>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        Propuesta del estudiante
      </Typography>

      {resolver.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {mensajeDeError(resolver.error, "No se pudo resolver la propuesta.")}
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3 }, border: "1px solid", borderColor: "divider", borderRadius: "9px" }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-start" }} justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600 }}>{propuesta.estudiante_nombre}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {propuesta.estudiante_email || "—"} · Propuesto el {formatFecha(propuesta.fecha_creacion)}
            </Typography>
          </Box>
          <EstadoChip
            label={propuesta.estado_display}
            tono={tonoDePostulacion(propuesta.estado)}
            icono={iconoDePostulacion(propuesta.estado)}
            denso
          />
        </Stack>

        {propuesta.mensaje && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {propuesta.mensaje}
          </Typography>
        )}

        {pendiente && admiteResolucion && (
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button size="small" variant="contained" color="error" disabled={resolver.isPending} onClick={() => abrir("rechazar")}>
              Rechazar
            </Button>
            <Button size="small" variant="contained" startIcon={<CheckIcon />} disabled={resolver.isPending} onClick={() => abrir("aceptar")}>
              Aceptar
            </Button>
          </Stack>
        )}
      </Paper>

      <Dialog open={confirmar !== null} onClose={() => setConfirmar(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{confirmar === "aceptar" ? "Aceptar la propuesta" : "Rechazar la propuesta"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmar === "aceptar" ? (
              <>
                Vas a aprobar el tema propuesto por <strong>{propuesta.estudiante_nombre}</strong> y quedará asignado a él.
              </>
            ) : (
              <>
                Vas a rechazar la propuesta de <strong>{propuesta.estudiante_nombre}</strong>. <strong>El tema se elimina</strong> y
                no se puede deshacer; el estudiante lo verá marcado como rechazado.
              </>
            )}
          </DialogContentText>
          {confirmar === "rechazar" && (
            <TextField
              label="Motivo (opcional)"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Por ejemplo: el alcance queda corto para una memoria; conversémoslo antes de que lo propongas de nuevo."
              helperText="Se lo mandamos en el correo, con tu nombre."
              fullWidth
              multiline
              minRows={3}
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmar(null)}>Cancelar</Button>
          <Button
            variant="contained"
            color={confirmar === "aceptar" ? "primary" : "error"}
            disabled={resolver.isPending}
            onClick={ejecutar}
          >
            {confirmar === "aceptar" ? "Aceptar" : "Rechazar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
